import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';

export interface TeacherCardData {
  id: string;
  name: string;
  image: string;
  speciality: string;
  biography?: string;
  /** Extra subjects beyond the primary badge (shows «+N مواد») */
  extraSubjectsCount?: number;
}

interface TeacherCardProps {
  teacher: TeacherCardData;
  onClick: (teacherId: string) => void;
  compact?: boolean;
  /** Base path for teacher profile link — default `/teacher/` */
  teacherPathPrefix?: string;
}

const TeacherCard: React.FC<TeacherCardProps> = ({
  teacher,
  onClick,
  teacherPathPrefix = '/teacher/',
}) => {
  const titleId = `teacher-card-title-${teacher.id}`;
  const bio = teacher.biography?.trim() || teacher.speciality;
  const prefix = teacherPathPrefix.endsWith('/') ? teacherPathPrefix : `${teacherPathPrefix}/`;
  const href = `${prefix}${teacher.id}`;

  return (
    <article
      role="listitem"
      className={`group flex h-full w-full min-w-0 cursor-pointer flex-col ${cardTheme.courseCard} rounded-xl`}
      aria-labelledby={titleId}
      onClick={() => onClick(teacher.id)}
    >
      <div className="relative aspect-[3/4] w-full shrink-0 overflow-hidden bg-[#EFE8DC]">
        <img
          src={teacher.image}
          alt={`صورة ${teacher.name}`}
          width={280}
          height={373}
          loading="lazy"
          decoding="async"
          sizes="(max-width: 639px) 70vw, 240px"
          className="absolute inset-0 z-[1] h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = '/default-teacher.jpg';
          }}
        />
        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-[#001848]/55 via-transparent to-transparent" />

        <div className="absolute start-2 top-2 z-[2] flex max-w-[90%] flex-wrap items-center gap-1">
          <span className="truncate rounded-full bg-[#C09048] px-2 py-0.5 text-[10px] font-bold text-white shadow-sm">
            {teacher.speciality}
          </span>
          {typeof teacher.extraSubjectsCount === 'number' && teacher.extraSubjectsCount > 0 && (
            <span className="rounded-full border border-white/40 bg-white/95 px-1.5 py-0.5 text-[10px] font-semibold text-[#001848] shadow-sm">
              +{teacher.extraSubjectsCount} مواد
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-1 flex-col border-t-[3px] border-t-[#C09048] bg-[#FAF7F0] p-3">
        <div className="space-y-1.5">
          <h3
            id={titleId}
            className={`line-clamp-2 text-sm font-bold leading-snug sm:text-base ${cardTheme.title}`}
            title={teacher.name}
          >
            {teacher.name}
          </h3>
          <p className={`line-clamp-2 text-xs leading-relaxed sm:text-sm ${cardTheme.subtitle}`}>{bio}</p>
          <div className={`mt-0.5 h-0.5 w-8 ${cardTheme.divider}`} />
        </div>

        <div className="mt-auto pt-3">
          <Link
            to={href}
            onClick={(e) => e.stopPropagation()}
            aria-label={`شوف كورسات ${teacher.name}`}
            className={`inline-flex min-h-10 w-full items-center justify-center gap-1.5 rounded-full px-3 text-xs sm:text-sm ${cardTheme.btnPrimary}`}
          >
            <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden />
            شوف الكورسات
          </Link>
        </div>
      </div>
    </article>
  );
};

export default TeacherCard;
